import React from 'react';
import { Receipt, Users, TrendingUp, FileText, PieChart, DollarSign, BarChart3, ShieldCheck } from 'lucide-react';
import './FinancesSidebar.css';

export type FinancesSection =
  | 'forecast'
  | 'overhead'
  | 'payroll'
  | 'project-pl'
  | 'progress-billing'
  | 'contractor-payables'
  | 'regulatory';

interface FinancesSidebarProps {
  selected: FinancesSection;
  onSelect: (section: FinancesSection) => void;
  counts: {
    overhead: number;
    payroll: number;
    contractorPayables?: number;
    designerInvoiceReviews?: number;
  };
  monthlyBurn: number;
  monthlyBurnBreakdown?: { overhead: number; budgeted: number };
  /** When false, Forecast / Overhead / Payroll are hidden. */
  showFullFinances?: boolean;
  /** Project P&L (admins, PMs, EAs). */
  showProjectPL?: boolean;
  /** Contractor payables section. */
  showPayables?: boolean;
  /** Admin-only: open the CPA portal preview. */
  onPreviewCpa?: () => void;
}

interface SidebarItem {
  id: FinancesSection;
  label: string;
  icon: React.ReactNode;
}

const ALL_ITEMS: SidebarItem[] = [
  { id: 'forecast', label: 'Forecast', icon: <TrendingUp size={15} /> },
  { id: 'overhead', label: 'OpEx', icon: <Receipt size={15} /> },
  { id: 'payroll', label: 'Payroll & Positions', icon: <Users size={15} /> },
  { id: 'project-pl', label: 'Project P&L', icon: <PieChart size={15} /> },
  { id: 'progress-billing', label: 'Progress Billing', icon: <BarChart3 size={15} /> },
  { id: 'contractor-payables', label: 'Contractor Payables', icon: <FileText size={15} /> },
  { id: 'regulatory', label: 'Regulatory', icon: <ShieldCheck size={15} /> },
];

const formatCurrency = (value: number) =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(value);

const FinancesSidebar: React.FC<FinancesSidebarProps> = ({
  selected,
  onSelect,
  counts,
  monthlyBurn,
  monthlyBurnBreakdown,
  showFullFinances = true,
  showProjectPL = false,
  showPayables = true,
  onPreviewCpa,
}) => {
  const items = ALL_ITEMS.filter((item) => {
    if (item.id === 'project-pl') return showProjectPL;
    if (item.id === 'contractor-payables') return showPayables;
    return showFullFinances;
  });

  return (
    <div className="fin-sidebar">
      <div className="fin-sidebar-header">
        <span className="fin-sidebar-label">Finances</span>
      </div>

      <nav className="fin-sidebar-nav" aria-label="Finances sections">
        {items.map(item => {
          const isActive = item.id === selected;
          let count: number | null = null;
          if (item.id === 'overhead') count = counts.overhead;
          if (item.id === 'payroll') count = counts.payroll;
          if (item.id === 'contractor-payables') count = counts.contractorPayables ?? null;
          const designerReviews =
            item.id === 'project-pl' ? counts.designerInvoiceReviews ?? 0 : 0;
          return (
            <button
              key={item.id}
              type="button"
              className={`fin-sidebar-item ${isActive ? 'active' : ''}`}
              onClick={() => onSelect(item.id)}
              aria-current={isActive ? 'page' : undefined}
            >
              <span className="fin-sidebar-item-icon">{item.icon}</span>
              <span className="fin-sidebar-item-label">{item.label}</span>
              {designerReviews > 0 && (
                <span
                  className="fin-sidebar-pl-reviews"
                  title={`${designerReviews} designer invoice${designerReviews === 1 ? '' : 's'} pending review`}
                  aria-label={`${designerReviews} designer invoice${designerReviews === 1 ? '' : 's'} pending review`}
                >
                  <DollarSign size={11} strokeWidth={2.4} aria-hidden />
                  {designerReviews}
                </span>
              )}
              {count !== null && count > 0 && (
                <span className="fin-sidebar-item-count">{count}</span>
              )}
            </button>
          );
        })}
      </nav>

      <div className="fin-sidebar-footer">
        {showFullFinances && (
          <>
            <span
              className="fin-sidebar-footer-label"
              title={
                monthlyBurnBreakdown
                  ? `Overhead ${formatCurrency(monthlyBurnBreakdown.overhead)} · Budgeted ${formatCurrency(monthlyBurnBreakdown.budgeted)}`
                  : undefined
              }
            >
              Monthly burn
            </span>
            <span
              className="fin-sidebar-footer-value"
              title={
                monthlyBurnBreakdown
                  ? `Overhead ${formatCurrency(monthlyBurnBreakdown.overhead)} · Budgeted ${formatCurrency(monthlyBurnBreakdown.budgeted)}`
                  : undefined
              }
            >
              {formatCurrency(monthlyBurn)}
            </span>
            {monthlyBurnBreakdown && (
              <span className="fin-sidebar-footer-split">
                Overhead {formatCurrency(monthlyBurnBreakdown.overhead)} · Budgeted{' '}
                {formatCurrency(monthlyBurnBreakdown.budgeted)}
              </span>
            )}
          </>
        )}
        {onPreviewCpa && (
          <button
            type="button"
            className="fin-sidebar-preview-btn"
            onClick={onPreviewCpa}
          >
            Preview CPA portal
          </button>
        )}
      </div>
    </div>
  );
};

export default FinancesSidebar;
